import { useState, useEffect, useCallback } from 'react';
// @mui
import Collapse from '@mui/material/Collapse';
// routes
import { usePathname } from 'src/routes/hooks';
import { useActiveLink } from 'src/routes/hooks/use-active-link';
//
import { NavListProps, NavConfigProps } from '../types';
import NavItem from './nav-item';
import { useTheme } from '@mui/system';

// ----------------------------------------------------------------------

type NavListRootProps = {
  data: NavListProps;
  depth: number;
  hasChild: boolean;
  config: NavConfigProps;
  defaultOpen?: boolean;
  lastChildren: any;
  activePaths: any;
};

export default function NavList({ data, depth, hasChild, config, defaultOpen, lastChildren }: any) {
  const pathname = usePathname();
  let active: any = useActiveLink(data.path, hasChild);
  const externalLink = data.path.includes('http');
  const theme = useTheme();

  // 👇 ajout pour supporter activePaths
  if (data.activePaths && Array.isArray(data.activePaths)) {
    active = data.activePaths.includes(pathname);
  }

  const [open, setOpen] = useState(active);
  const [urlToogle, setUrlToogle]: any = useState<string | undefined>(undefined);

  // Mettre à jour urlToogle à chaque fois que pathname change
  useEffect(() => {
    if (active) {
      setUrlToogle(data.path); // Mettre à jour urlToogle uniquement si l'élément est actif
    }
  }, [pathname, active, data.path]);

  const handleToggle = useCallback(() => {
    setOpen((prev: any) => !prev); // On inverse l'état de l'ouverture
    if (!open) {
      setUrlToogle(data.path); // Mettre à jour urlToogle quand on ouvre
    } else {
      setUrlToogle(undefined); // Réinitialiser quand on ferme
    }
  }, [open, data.path]);


  return (
    <>
      <NavItem
        item={data}
        depth={depth}
        style={{ color: theme.palette.mode === "dark" ? 'white' : "#637381" }}
        open={defaultOpen || open}
        active={active}
        externalLink={externalLink}
        onClick={handleToggle}
        isChild={depth > 1}
        isLastChild={lastChildren}
        config={config}
        urlToogle={urlToogle} // Passer l'URL ici
      />

      {hasChild && (
        <Collapse in={defaultOpen || open} unmountOnExit>
          <NavSubList data={data.children} depth={depth} config={config} />
        </Collapse>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

type NavListSubProps = {
  data: NavListProps[];
  depth: number;
  config: NavConfigProps;
};

function NavSubList({ data, depth, config }: NavListSubProps) {
  return (
    <>
      {data.map((list: any) => (
        <NavList
          key={list.title + list.path}
          lastChildren={list.lastChildren}
          data={list}
          depth={depth + 1}
          hasChild={!!list.children}
          config={config}
        />
      ))}
    </>
  );
}
